import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Container, Grid, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../../../components/Header";
import { colorGrid } from "../../../theme";
import {
  CHECK_STATE_COLOR,
  checkState,
  formatSince,
} from "../../../utils/entity/checkHealth";
import { useLocalState } from "../../../utils/hooks/global/useLocalStorage";
import CustomButton from "../../global/CustomButton";
import CustomTabsTable from "../../global/CustomTabsTable";
import { Loading } from "../../global/Loading";
import PauseCheckButton from "../../global/PauseCheckButton";

const tabs = {
  failing: {
    func: (data) => data.filter((row) => row.state === "failing"),
    label: "Failing",
    color: "yellow",
  },
  paused: {
    func: (data) => data.filter((row) => row.state === "paused"),
    label: "Paused",
    color: "gray",
  },
  working: {
    func: (data) => data.filter((row) => row.state === "working"),
    label: "Working",
    color: "green",
  },
  all: {
    func: (data) => data,
    label: "All",
    color: "default",
  },
};

const toRow = (health) => ({
  ...health,
  id: health.slug,
  state: checkState(health),
});

// Every exchange status check with its health, so broken checks can be
// fixed or paused in one place
function AdminStatusChecks() {
  const [jwt] = useLocalState(null, "jwt");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${backendUrl}/exchanges/check-health/`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + jwt,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Could not load status checks (${response.status})`);
        }
        return response.json();
      })
      .then((data) => setRows(data.map(toRow)))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [jwt, backendUrl]);

  const handleChecksPausedChange = useCallback((health) => {
    setRows((prev) =>
      prev.map((row) => (row.slug === health.slug ? toRow(health) : row))
    );
  }, []);

  const columns = [
    {
      field: "name",
      headerName: "Exchange",
      flex: 1.4,
      minWidth: 170,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          {params.row.logo && (
            <img
              src={params.row.logo}
              alt=""
              style={{ width: 25, height: 25, marginRight: 10, flexShrink: 0 }}
            />
          )}
          <Link
            to={`/entities/${params.row.slug}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {params.value}
          </Link>
        </Box>
      ),
    },
    {
      field: "state",
      headerName: "Check",
      flex: 0.7,
      renderCell: (params) => {
        const color = colorGrid[CHECK_STATE_COLOR[params.value]];
        return (
          <Box
            component="span"
            sx={{
              bgcolor: color.background,
              color: color.text,
              borderRadius: "4px",
              px: 0.75,
              py: 0.25,
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "checkType",
      headerName: "Type",
      flex: 0.5,
      minWidth: 80,
      valueFormatter: (params) => (params.value === "scrape" ? "Scrape" : "API"),
    },
    {
      field: "lastSuccessfulCheckAt",
      headerName: "Last worked",
      type: "dateTime",
      flex: 0.9,
      minWidth: 120,
      valueGetter: (params) =>
        params.value ? new Date(params.value) : null,
      renderCell: (params) =>
        params.row.state === "paused"
          ? `paused ${formatSince(params.row.checksPausedAt)}`
          : formatSince(params.value),
    },
    {
      field: "checkErrorCount",
      headerName: "Fails in a row",
      type: "number",
      flex: 0.6,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "lastCheckError",
      headerName: "Last error",
      flex: 2.4,
      minWidth: 240,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <Typography
              component="code"
              noWrap
              sx={{ fontSize: 12, fontFamily: "monospace", minWidth: 0 }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        ) : (
          <Typography color="text.disabled" sx={{ fontSize: 13 }}>
            —
          </Typography>
        ),
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      flex: 0.6,
      minWidth: 100,
      align: "right",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <CustomButton
            buttonText="Edit check"
            icon={EditOutlinedIcon}
            onClick={() =>
              navigate(`/entities/${params.row.slug}?edit=integration`)
            }
          />
          <PauseCheckButton
            slug={params.row.slug}
            name={params.row.name}
            paused={params.row.state === "paused"}
            onChange={handleChecksPausedChange}
          />
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "75vh",
        }}
      >
        <Loading size={50} thickness={6} />
      </Box>
    );
  }

  return (
    <Container maxWidth={"xl"}>
      <Header
        title="Status checks"
        subtitle="Fix checks that can't read the deposit/withdrawal status, or pause them if the API is gone."
      />
      {error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CustomTabsTable
              columns={columns}
              tabs={tabs}
              defaultValue="failing"
              name={"adminStatusChecks"}
              data={rows}
            />
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default AdminStatusChecks;
